import { useEffect, useMemo, useState } from 'react'
import { FileText, DownloadSimple, Warning, ArrowsClockwise, Trash, FilePdf, Eye, EyeSlash } from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { generateSyb7Html } from './syb7Report'
import { generateArpsiHtml } from './arpsiReport'
import FloodMap from '../sensors/FloodMap'
import NaturalRisksMap from '../sensors/NaturalRisksMap'
import BiodiversityMap from '../sensors/BiodiversityMap'

const C = {
  text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8',
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A',
  bg: '#FFFFFF', bgAlt: '#FAF7F3',
}
const CARD = { background: C.bg, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: `1px solid ${C.border}` }
const FONT = "'DM Sans', sans-serif"

// Catálogo de informes técnicos disponibles
const AVAILABLE_REPORTS = [
  {
    id: 'syb7_riesgos_naturales',
    title: 'Informe SyB 7 · Riesgos Naturales',
    description: 'Evaluación de las cuatro amenazas naturales del emplazamiento (sismicidad, incendios forestales, meteorología extrema y estabilidad geológica) con fuentes oficiales estatales (IGN, MITECO, AEMET, IGME) y de la CCAA competente del emplazamiento. Requerido para el Requisito SyB 7 de BREEAM ES NC 2015.',
    filename_prefix: 'Informe_SyB7_Riesgos_Naturales',
    generator: 'syb7',
    requiredFields: ['latitude', 'longitude', 'municipality'],
    hasMap: true,
  },
  {
    id: 'arpsi_inundacion',
    title: 'Informe ARPSI · Riesgo de Inundación',
    description: 'Verificación del activo contra el Sistema Nacional de Cartografía de Zonas Inundables (SNCZI) del MITECO — capas fluviales T=10 / T=100 / T=500 y marinas T=100 / T=500 según Directiva 2007/60/CE y RD 903/2010. Alimenta el datapoint ESRS E1-9 (riesgos físicos climáticos agudos) y el detector D11 del bloque IROs.',
    filename_prefix: 'Informe_ARPSI_Inundacion',
    generator: 'arpsi',
    requiredFields: ['latitude', 'longitude'],
    hasMap: true,
  },
  {
    id: 'biodiversidad_areas_protegidas',
    title: 'Informe Biodiversidad · Áreas Protegidas',
    description: 'Mapeo del emplazamiento contra Red Natura 2000 (LIC/ZEC/ZEPA), Red de Parques Nacionales, Reservas de la Biosfera, Hábitats de Interés Comunitario (Anexo I Directiva Hábitats), Zonas Húmedas Ramsar, Montes de Utilidad Pública y Red Canaria de Espacios Naturales Protegidos. Alimenta ESRS E4-5, EU Taxonomy Obj 6 (DNSH), TNFD LEAP (Locate), BREEAM USO 02/03/05 y GRI 304.',
    filename_prefix: 'Informe_Biodiversidad_Areas_Protegidas',
    generator: 'biodiversidad',
    requiredFields: ['latitude', 'longitude'],
    hasMap: true,
  },
  // Aquí se añadirán más informes en el futuro (acústico, geotécnico, etc.)
]

export default function InformesTecnicos({ project }) {
  const [settings, setSettings] = useState(null)
  const [existingReports, setExistingReports] = useState([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(null)
  const [msg, setMsg] = useState(null)
  const [mapVisible, setMapVisible] = useState(new Set())  // ids de informes con mapa desplegado

  const load = async () => {
    if (!project?.id) return
    setLoading(true)
    try {
      const [s, ev, projFresh, projDetails] = await Promise.all([
        supabase.from('operational_iro_settings').select('*').eq('project_id', project.id).maybeSingle(),
        supabase.from('evidences')
          .select('id, file_name, file_path, storage_bucket, uploaded_at')
          .eq('project_id', project.id)
          .like('file_name', 'Informe_%')
          .order('uploaded_at', { ascending: false }),
        // Datos geográficos frescos de la tabla projects, por si el prop está
        // desactualizado (el usuario acaba de guardar coordenadas en Datos Proyecto)
        supabase.from('projects')
          .select('latitude, longitude, municipality, province')
          .eq('id', project.id).maybeSingle(),
        // JSON del formulario ProjectDetails (guardado en evaluations) para
        // recuperar zona climática CTE, Köppen y otros datos contextuales
        supabase.from('evaluations')
          .select('justificacion')
          .eq('project_id', project.id)
          .eq('criteria_code', 'PROJECT-DETAILS')
          .maybeSingle(),
      ])
      const geo = projFresh?.data || project
      let projDetailsData = {}
      if (projDetails?.data?.justificacion) {
        try { projDetailsData = JSON.parse(projDetails.data.justificacion) } catch {}
      }
      // Mezclar: los datos geográficos vienen de la tabla projects
      // (rellenados desde Datos del Proyecto) y tienen prioridad sobre los
      // que hubiera en operational_iro_settings. Los datos técnicos
      // (aceleración sísmica, ARPSI, etc.) siguen viniendo de settings.
      const merged = { ...(s.data || {}) }
      if (geo.latitude     != null) merged.latitude     = Number(geo.latitude)
      if (geo.longitude    != null) merged.longitude    = Number(geo.longitude)
      if (geo.municipality)         merged.municipality = geo.municipality
      if (geo.province)             merged.province     = geo.province
      // Si no había address explícita pero sí municipio, se usa el municipio
      if (!merged.address && geo.municipality) merged.address = geo.municipality
      // Zona climática CTE y Köppen desde el JSON de ProjectDetails
      if (projDetailsData.zonaClimaticaCTE) merged.zona_climatica_cte = projDetailsData.zonaClimaticaCTE
      if (projDetailsData.zonaClimatica)    merged.zona_climatica_koppen = projDetailsData.zonaClimatica
      if (projDetailsData.direccion && !merged.address) merged.address = projDetailsData.direccion
      setSettings(merged)
      setExistingReports(ev.data || [])
    } catch (e) {
      setMsg({ ok: false, text: e.message })
    } finally { setLoading(false) }
  }
  // load depende solo de project?.id (ya en deps).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [project?.id])

  const canGenerate = (report) => {
    if (!settings) return { ok: false, reason: 'Rellena primero los datos de ubicación geográfica en Datos del Proyecto → C. Ubicación geográfica.' }
    const humanLabels = {
      latitude: 'Latitud', longitude: 'Longitud',
      address: 'Dirección', municipality: 'Municipio',
    }
    for (const f of report.requiredFields || []) {
      if (settings[f] == null || settings[f] === '') {
        const label = humanLabels[f] || f
        return { ok: false, reason: `Falta el campo "${label}" en Datos del Proyecto → C. Ubicación geográfica.` }
      }
    }
    // Específicos SyB 7
    if (report.id === 'syb7_riesgos_naturales') {
      if (settings.seismic_ab_ncse02 == null) {
        return { ok: false, reason: 'Falta la aceleración sísmica básica NCSE-02 del municipio. Guarda el municipio en Datos del Proyecto y se calculará automáticamente.' }
      }
    }
    // Específicos ARPSI
    if (report.id === 'arpsi_inundacion') {
      if (!settings.arpsi_status || settings.arpsi_status === 'not_checked') {
        return { ok: false, reason: 'Falta la verificación ARPSI (SNCZI). Guarda las coordenadas en Datos del Proyecto y la comprobación contra el WMS oficial del MITECO se dispara automáticamente.' }
      }
    }
    return { ok: true }
  }

  const handleGenerate = async (report) => {
    setMsg(null)
    setGenerating(report.id)
    try {
      const { data: u } = await supabase.auth.getUser()
      const user_id = u?.user?.id
      if (!user_id) throw new Error('No autenticado')

      // Cargar datos meteorológicos si el informe los necesita
      let weatherRows = []
      if (report.id === 'syb7_riesgos_naturales') {
        const w = await supabase.from('weather_hdd_cdd')
          .select('year, month, hdd_15_5, cdd_22, station_name, station_dist_km')
          .eq('project_id', project.id)
        weatherRows = w.data || []
      }

      // Generar HTML
      let html = ''
      if (report.generator === 'syb7') {
        html = generateSyb7Html({ project, settings, weatherRows })
      } else if (report.generator === 'arpsi') {
        html = generateArpsiHtml({ project, settings })
      } else if (report.generator === 'biodiversidad') {
        const { generateBiodiversidadHtml } = await import('./biodiversidadReport')
        // Cargar último snapshot cacheado del proyecto para incluir los datos
        // reales (Natura 2000, especies GBIF, ENCORE) en el HTML del informe.
        const { data: snap } = await supabase
          .from('biodiversity_snapshots')
          .select('fetched_at, radius_km, summary, wdpa_areas, gbif_species, encore_deps, errors')
          .eq('project_id', project.id)
          .order('fetched_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        html = generateBiodiversidadHtml({ project, settings, snapshot: snap })
      } else {
        throw new Error('Generador no implementado: ' + report.generator)
      }

      // Abrir ventana con vista previa + botón imprimir
      const win = window.open('', '_blank', 'width=1000,height=1400')
      if (!win) throw new Error('El navegador ha bloqueado la ventana emergente. Permite popups para este sitio.')
      win.document.open()
      win.document.write(html)
      win.document.close()

      // Guardar el HTML como evidencia en el proyecto para trazabilidad
      const dateStr = new Date().toISOString().slice(0, 10)
      const fileName = `${report.filename_prefix}_${dateStr}.html`
      const path = `${user_id}/reports/${project.id}/${Date.now()}_${fileName}`
      const blob = new Blob([html], { type: 'text/html; charset=utf-8' })

      const { error: upErr } = await supabase.storage.from('evidences').upload(path, blob, { contentType: 'text/html; charset=utf-8', upsert: false })
      if (upErr) {
        console.warn('Storage upload:', upErr.message)
      } else {
        // Cada tipo de informe se asocia a un criterio BREEAM diferente
        const evConfig = report.generator === 'arpsi' ? {
          code: 'CNT 3', name: 'Riesgo de inundación', category: 'Contaminación', pmax: 5, subc: 3,
        } : report.generator === 'biodiversidad' ? {
          code: 'USO 02', name: 'Valor ecológico del emplazamiento', category: 'Uso del suelo y ecología', pmax: 2, subc: 2,
        } : {
          code: 'SyB 7', name: 'Riesgos naturales', category: 'Salud y Bienestar', pmax: 1, subc: 2,
        }
        const evLookup = await supabase.from('evaluations')
          .select('id')
          .eq('project_id', project.id)
          .eq('criteria_code', evConfig.code)
          .maybeSingle()
        let evaluationId = evLookup.data?.id
        if (!evaluationId) {
          const ins = await supabase.from('evaluations').insert({
            project_id: project.id, user_id,
            category: evConfig.category, criteria_code: evConfig.code,
            criteria_name: evConfig.name,
            points_obtained: 0, points_max: evConfig.pmax, cumple: false,
            justificacion: '{}', subcriteria_cumplidos: 0, subcriteria_total: evConfig.subc,
          }).select('id').single()
          evaluationId = ins.data?.id
        }
        await supabase.from('evidences').insert({
          evaluation_id: evaluationId, project_id: project.id, user_id,
          file_name: fileName, file_path: path, file_type: 'text/html', storage_bucket: 'evidences',
        })
        setMsg({ ok: true, text: `Informe generado y guardado como evidencia (${fileName}).` })
        load()
      }
    } catch (e) {
      setMsg({ ok: false, text: e.message })
    } finally {
      setGenerating(null)
    }
  }

  const handleOpen = async (r) => {
    try {
      // Descargar el fichero desde Storage y re-abrirlo como Blob URL con el
      // MIME correcto según la extensión, para que el navegador lo renderice
      // (PDF nativo · HTML como página) en lugar de mostrar los bytes crudos.
      const { data, error } = await supabase.storage.from(r.storage_bucket || 'evidences').createSignedUrl(r.file_path, 3600)
      if (error) throw error
      const res = await fetch(data.signedUrl)
      if (!res.ok) throw new Error('No se pudo descargar (HTTP ' + res.status + ')')
      const ext = (r.file_name || r.file_path || '').split('.').pop().toLowerCase()
      const mimeByExt = {
        pdf: 'application/pdf',
        html: 'text/html; charset=utf-8',
        htm: 'text/html; charset=utf-8',
        txt: 'text/plain; charset=utf-8',
        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
      }
      const mime = mimeByExt[ext] || 'application/octet-stream'
      const payload = mime.startsWith('text/') ? await res.text() : await res.blob()
      const blob = payload instanceof Blob ? new Blob([payload], { type: mime }) : new Blob([payload], { type: mime })
      const blobUrl = URL.createObjectURL(blob)
      const win = window.open(blobUrl, '_blank')
      if (!win) throw new Error('Popup bloqueado por el navegador. Permite ventanas emergentes para este sitio.')
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000)
    } catch (e) {
      setMsg({ ok: false, text: e.message })
    }
  }
  const handleDelete = async (r) => {
    if (!window.confirm(`¿Borrar el informe "${r.file_name}"?`)) return
    await supabase.storage.from(r.storage_bucket || 'evidences').remove([r.file_path])
    await supabase.from('evidences').delete().eq('id', r.id)
    load()
  }

  // canGenerate solo lee `settings` (ya en deps); no la envolvemos en
  // useCallback para no complicar el flujo de renderizado.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const projectReadyRow = useMemo(() => AVAILABLE_REPORTS.map(r => ({ ...r, status: canGenerate(r) })), [settings])

  if (!project?.id) {
    return (
      <div style={{ ...CARD, fontFamily: FONT, color: C.muted }}>
        Selecciona un proyecto para gestionar sus informes técnicos.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: FONT }}>
      {/* Header */}
      <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA' }}>
        <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: 500 }}>
          Consultoría especializada · HAUS
        </div>
        <div style={{ fontSize: '1.2rem', fontWeight: 600 }}>Informes técnicos del especialista</div>
        <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.2rem' }}>
          Documentos técnicos generados por HAUS Healthy Buildings como consultor especializado (independiente al rol del asesor BREEAM). Sirven como evidencia documental para los requisitos correspondientes.
        </div>
      </div>

      {msg && (
        <div style={{ background: msg.ok ? '#F0F5EC' : '#FFF1ED', border: `1px solid ${msg.ok ? C.olive : C.coral}`, borderRadius: 6, padding: '0.55rem 0.9rem', color: msg.ok ? '#2E6E3F' : C.coral, fontSize: '0.82rem' }}>
          {msg.text}
        </div>
      )}

      {/* Informes disponibles */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 0.7rem', fontSize: '0.95rem', color: C.text }}>Informes disponibles para generar</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
          {projectReadyRow.map(r => {
            const showMap = mapVisible.has(r.id)
            const canShowMap = r.hasMap && settings?.latitude != null && settings?.longitude != null
            return (
              <div key={r.id} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: '0.8rem 1rem', background: C.bgAlt }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', fontWeight: 600, color: C.text }}>
                      <FilePdf size={16} weight="regular" style={{ color: C.olive }} /> {r.title}
                    </div>
                    <div style={{ fontSize: '0.76rem', color: C.muted, marginTop: 4, lineHeight: 1.45 }}>{r.description}</div>
                    {!r.status.ok && (
                      <div style={{ fontSize: '0.72rem', color: C.coral, marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Warning size={12} /> {r.status.reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
                    {canShowMap && (
                      <button onClick={() => {
                        setMapVisible(prev => {
                          const next = new Set(prev)
                          if (next.has(r.id)) next.delete(r.id); else next.add(r.id)
                          return next
                        })
                      }}
                        style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 5, padding: '6px 12px', fontSize: '0.74rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        {showMap ? <EyeSlash size={12} /> : <Eye size={12} />}
                        {showMap ? 'Ocultar mapa' : 'Ver mapa'}
                      </button>
                    )}
                    <button onClick={() => handleGenerate(r)} disabled={!r.status.ok || generating === r.id}
                      style={{ background: r.status.ok ? C.olive : '#B0A89E', color: '#FFF', border: 'none', borderRadius: 5, padding: '8px 14px', fontSize: '0.78rem', fontWeight: 600, cursor: r.status.ok ? 'pointer' : 'not-allowed', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {generating === r.id ? <ArrowsClockwise size={13} /> : <FileText size={13} />}
                      {generating === r.id ? 'Generando…' : 'Generar Informe'}
                    </button>
                  </div>
                </div>
                {showMap && canShowMap && (
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: `1px dashed ${C.border}` }}>
                    {r.generator === 'arpsi' ? (
                      <FloodMap
                        latitude={settings.latitude}
                        longitude={settings.longitude}
                        addressLabel={settings.address || `${settings.municipality || ''}`.trim()}
                        height={360}
                      />
                    ) : r.generator === 'biodiversidad' ? (
                      <BiodiversityMap
                        projectId={project.id}
                        latitude={settings.latitude}
                        longitude={settings.longitude}
                        addressLabel={settings.address || `${settings.municipality || ''}`.trim()}
                        province={settings.province}
                        height={420}
                      />
                    ) : (
                      <NaturalRisksMap
                        latitude={settings.latitude}
                        longitude={settings.longitude}
                        addressLabel={settings.address || `${settings.municipality || ''}`.trim()}
                        province={settings.province}
                        height={360}
                      />
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Informes ya generados */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 0.7rem', fontSize: '0.95rem', color: C.text }}>Informes ya generados para este proyecto ({existingReports.length})</h3>
        {loading ? (
          <div style={{ color: C.muted, fontSize: '0.82rem' }}>Cargando…</div>
        ) : existingReports.length === 0 ? (
          <div style={{ color: C.muted, fontSize: '0.82rem', padding: '0.5rem 0', background: C.bgAlt, border: `1px dashed ${C.border}`, borderRadius: 6 }}>
            <div style={{ padding: '0.75rem' }}>Aún no se ha generado ningún informe técnico para este proyecto.</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: '#EDE7DE', color: '#5C4A3A' }}>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Archivo</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>Fecha</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', width: '160px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {existingReports.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 === 0 ? C.bg : C.bgAlt, borderBottom: '1px solid #F0EBE2' }}>
                  <td style={{ padding: '0.4rem 0.5rem', fontFamily: "'IBM Plex Mono', monospace", color: C.text }}>{r.file_name}</td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center', color: C.muted }}>{r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString('es-ES') : '—'}</td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button onClick={() => handleOpen(r)} title="Abrir" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.olive, padding: '3px 6px', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <DownloadSimple size={13} /> Abrir
                    </button>
                    <button onClick={() => handleDelete(r)} title="Borrar" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.coral, padding: '3px 6px', marginLeft: 4 }}>
                      <Trash size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
